import React from 'react';
import { AccentSpinner } from '../AccentSpinner';
import AccentIcons from '../../icons/Icons';
import { t } from '../../services/HelperService';
import { AccentPromptChat } from './AccentPromptChat';
import { AccentPromptHistory } from './AccentPromptHistory';
import { useAiChatAssistant, safeString } from './useAiChatAssistant';
import { sendAssistantChatTurn, parseAssistantChatResponse } from './assistantChatApi';
import { promptAndReportAssistantBug } from './reportAssistantBug';

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/**
 * The second audience's view of an assistant answer: the oracle's technical findings
 * (member names, JSONPath, values) that the visible message deliberately omits.
 * Collapsed by default — the average user never needs it, the curious one is a click
 * away. Rendered under the bubble via AccentPromptHistory's renderMessageExtra, so
 * profiles/hosts that never attach `details` to a message are untouched.
 */
const TechnicalDetailsExpander = ({ details }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ maxWidth: '85%', marginTop: 2 }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#6c757d', userSelect: 'none' }}
      >
        {open ? '\u25BE' : '\u25B8'} {t('Technical details')}
      </div>
      {open && (
        <div
          style={{
            marginTop: 2,
            padding: '6px 8px',
            fontSize: 11,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: 6,
            color: '#495057',
          }}
        >
          {details}
        </div>
      )}
    </div>
  );
};

const renderMessageDetails = (m) => (
  safeString(m?.details).trim() ? <TechnicalDetailsExpander details={safeString(m.details)} /> : null
);

/**
 * Docked (non-modal) client for CHAT-ONLY query-shaping session profiles, so the user
 * can converse with an assistant while still working in the hosting screen.
 *
 * This is a deliberately SLIM sibling of AccentQueryShapingAssistantDlg: it sends just
 * the chat-only subset of the session request (see buildAssistantChatRequest) and
 * renders the reply text, notices and context warnings. Profiles with ARTIFACTS —
 * queries, outputs, checklists, previews — belong in the dialog; this panel must not
 * grow toward that. It shares the transcript machinery (useAiChatAssistant), the
 * composer/history controls and the chat protocol with the dialog — including the
 * hidden "tool" records the server round-trips so later turns build on the oracle's
 * previous brief — plus the "Send Chat as Bug" flow (the bug icon in the header).
 *
 * Profile configuration:
 * - sessionProfile: the profile key (server pairs it with its C# session profile)
 * - title / placeholder: display strings (keep in sync with the profile's dialogTitle
 *   and chatPlaceholder in queryShapingSessionProfiles.js — imported as props so hosts
 *   don't pull the registry's preview-component imports into their bundle)
 * - buildRequestFields: FUNCTION returning the profile's extra request fields (e.g.
 *   { explainContextJson }), called on EVERY send so the assistant always sees the
 *   host's current state
 * - promptIntent: 'ask' (default) for pure Q&A profiles — skips the AI's
 *   question-vs-change clarifying round and is server-enforced read-only
 * - headerActions: optional extra header elements, rendered left of the built-ins
 *
 * Session continuity: the host holds the session object ({ diagnosticSessionID,
 * messages }) across toggles via session/onSessionChange, so closing and reopening the
 * panel resumes the same conversation — and the host's dispose path ends the server
 * session (see ProductExplainerLauncher.dispose).
 */
export const AccentAssistantChatPanel = ({
  sessionProfile,
  title,
  placeholder,
  buildRequestFields,
  promptIntent = 'ask',
  hostLabel,
  headerActions,
  session,
  onSessionChange,
  onClose,
}) => {

  const { chatMessages, setChatMessages, pushMsg, buildServerMessages } = useAiChatAssistant({});
  const [busy, setBusy] = React.useState(false);
  const [reportingBug, setReportingBug] = React.useState(false);

  // One id per SESSION (kept across close/reopen via the stored session object) so the
  // server-side diagnostic capture and cached definitions stay correlated.
  const diagnosticSessionIdRef = React.useRef(safeString(session?.diagnosticSessionID).trim() || `qs_${makeId()}`);

  // Resume a previous open's transcript. Seeding belongs to mount only.
  React.useEffect(() => {
    if (Array.isArray(session?.messages) && session.messages.length > 0) {
      setChatMessages(session.messages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the transcript on every change so a toggle-close never loses the
  // conversation (there is no explicit "close payload" like the dialog's).
  const onSessionChangeRef = React.useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;
  React.useEffect(() => {
    onSessionChangeRef.current?.({ diagnosticSessionID: diagnosticSessionIdRef.current, messages: chatMessages });
  }, [chatMessages]);

  const onSendPrompt = React.useCallback(async ({ text, history, images }) => {
    try {
      setBusy(true);
      const res = await sendAssistantChatTurn({
        prompt: safeString(text),
        messages: buildServerMessages(history, text),
        sessionProfile: safeString(sessionProfile),
        diagnosticSessionID: diagnosticSessionIdRef.current,
        promptIntent,
        hostLabel: safeString(hostLabel ?? title),
        // Screenshots pasted with this message (downscaled data URLs); the server
        // shows them to its models as images.
        promptImages: Array.isArray(images) ? images : [],
        // The profile's own request fields, snapshotted per SEND.
        ...(buildRequestFields?.() ?? {}),
      });

      const parsed = parseAssistantChatResponse(res);

      if (parsed.toolRecord) {
        pushMsg('tool', parsed.toolRecord);
      }

      // details rides the message object (and the persisted session transcript), so
      // the expander survives close/reopen with the rest of the conversation.
      pushMsg('assistant', parsed.message || t('I could not produce a response. Please try rephrasing.'),
        parsed.technicalDetails ? { details: parsed.technicalDetails } : undefined);

      for (const notice of parsed.notices) {
        pushMsg(notice.severity === 'error' ? 'error' : 'system', notice.text);
      }

      if (parsed.contextWarnings.length > 0) {
        pushMsg('system', `${t('Assistant context is incomplete')}: ${parsed.contextWarnings.join(' \u2022 ')}`);
      }

      return true;
    } catch (err) {
      // Server errors may be localization keys (e.g. the AI usage limit); resolve them.
      const raw = safeString(err?.message) || safeString(err);
      pushMsg('error', raw ? t(raw) : t('AI request failed.'));
      return false;
    } finally {
      setBusy(false);
    }
  }, [buildRequestFields, buildServerMessages, hostLabel, promptIntent, pushMsg, sessionProfile, title]);

  const doReportBug = React.useCallback(async () => {
    setReportingBug(true);
    try {
      await promptAndReportAssistantBug({
        diagnosticSessionID: diagnosticSessionIdRef.current,
        hostLabel: safeString(hostLabel ?? title),
        chatMessages,
        pushMsg,
      });
    } finally {
      setReportingBug(false);
    }
  }, [chatMessages, hostLabel, pushMsg, title]);

  return (
    <div
      data-tagname="assistantChatPanel"
      style={{
        display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
        border: '1px solid #dee2e6', borderRadius: 6, padding: 10, background: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {headerActions}
          <AccentIcons.Bug
            noPadding
            top={0}
            disabled={reportingBug || chatMessages.length === 0}
            onClick={doReportBug}
            toolTipText={t('Send Chat as Bug — emails this conversation and a diagnostic log of the session to your support contact')}
          />
          {onClose && <AccentIcons.Clear noPadding top={0} onClick={onClose} toolTipText={t('Close the assistant')} />}
        </div>
      </div>
      {busy && (
        <div style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, background: '#e3f2fd', color: '#1565c0', marginBottom: 6 }}>
          <AccentSpinner size="sm" /> {t('Thinking...')}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <AccentPromptHistory messages={chatMessages} fillHeight renderMessageExtra={renderMessageDetails} />
      </div>
      <div style={{ borderTop: '1px solid #dee2e6', paddingTop: 8, marginTop: 6 }}>
        <AccentPromptChat
          disabled={busy}
          messages={chatMessages}
          onMessagesChange={setChatMessages}
          onSendPrompt={onSendPrompt}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

// The emulated-sticky clamps: never above the app's fixed header, small gap to the
// viewport bottom.
const PANEL_TOP_MIN = 60;
const PANEL_BOTTOM_GAP = 12;

/**
 * Two-pane layout for hosting an assistant chat panel BESIDE editable content: the
 * host's content on the left, a drag-resizable divider, and the panel element on the
 * right — kept in view while the user scrolls the hosting screen.
 *
 * Stickiness is EMULATED, not CSS `position: sticky`: hosts sit inside scroll/
 * overflow containers (tab content, the app's scrolling body) that break sticky. The
 * right column only reserves layout space; the panel itself is `position: fixed`,
 * anchored to the column's measured rect and re-clamped on every scroll (capture
 * listener, so it works whichever container scrolls) — in-flow while the column's top
 * is on screen, pinned below the app header once it scrolls past.
 */
export const AccentAssistantDock = ({ children, panel }) => {

  const [panelWidth, setPanelWidth] = React.useState(420);
  const hostRef = React.useRef(null);
  const [anchor, setAnchor] = React.useState(null);

  React.useLayoutEffect(() => {
    const update = () => {
      const rect = hostRef.current?.getBoundingClientRect();
      // Zero width means a hidden ancestor (e.g. an inactive tab): keep the last anchor,
      // the display:none ancestor already hides the fixed panel.
      if (!rect || rect.width === 0) return;
      const next = {
        top: Math.round(Math.max(PANEL_TOP_MIN, rect.top)),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      };
      setAnchor((prev) => (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) ? prev : next);
    };
    update();
    window.addEventListener('resize', update);
    // Capture phase: scroll events don't bubble, and which ancestor scrolls here
    // depends on the host (window, view body, tab content).
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [panelWidth]);

  const onDividerMouseDown = React.useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMove = (moveEvent) => {
      setPanelWidth(Math.min(700, Math.max(300, startWidth + (startX - moveEvent.clientX))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        {children}
      </div>
      <div
        onMouseDown={onDividerMouseDown}
        style={{ flex: '0 0 9px', cursor: 'col-resize', display: 'flex', justifyContent: 'center' }}
        data-tagname="assistantSplitBar"
      >
        <div style={{ width: 3, background: '#dee2e6', borderRadius: 2 }} />
      </div>
      {/* Layout placeholder only — the panel is fixed to this column's measured rect. */}
      <div ref={hostRef} style={{ flex: `0 0 ${panelWidth}px`, minWidth: 300 }}>
        <div style={anchor
          ? { position: 'fixed', top: anchor.top, left: anchor.left, width: anchor.width, bottom: PANEL_BOTTOM_GAP, zIndex: 5 }
          : { height: 'calc(100vh - 170px)', minHeight: 320 }}
        >
          {panel}
        </div>
      </div>
    </div>
  );
};
